import { useState } from 'react';
import type { ReactElement } from 'react';
import styles from './McpSetupGuide.module.css';

type Platform = {
  readonly label: string;
  readonly key: string;
  readonly command: string;
  readonly group: 'web' | 'desktop' | 'cli';
};

const PLATFORMS: ReadonlyArray<Platform> = [
  // ── Web Apps ──
  {
    key: 'claude-web',
    label: 'Claude.ai',
    group: 'web',
    command: [
      '1. Go to claude.ai/settings',
      '2. Click Integrations (left sidebar)',
      '3. Scroll down and click "Add custom connector"',
      '4. Enter the server URL:',
      '   https://mcp.event.black',
      '5. Click Connect',
      '6. Complete the OAuth sign-in flow',
      '7. The integration appears in all conversations',
      '',
      'Works on web and mobile. Requires a paid plan.',
    ].join('\n'),
  },
  {
    key: 'chatgpt-web',
    label: 'ChatGPT',
    group: 'web',
    command: [
      '1. Go to chatgpt.com/settings',
      '2. Click Apps and Connectors',
      '3. Scroll to the bottom, click Create',
      '4. Enter the server URL:',
      '   https://mcp.event.black',
      '5. Set authentication to OAuth',
      '6. Save and click Refresh to verify tools',
      '',
      'Requires Developer Mode (Pro, Team, Enterprise, or Edu).',
      'Enable it in Settings first if you don\'t see Connectors.',
    ].join('\n'),
  },

  // ── Desktop Apps ──
  {
    key: 'claude-desktop',
    label: 'Claude Desktop',
    group: 'desktop',
    command: [
      'Option A — Connectors UI (recommended):',
      '  Settings \u2192 Connectors \u2192 Add custom connector',
      '  Enter URL: https://mcp.event.black',
      '',
      'Option B — Config file:',
      '  Claude \u2192 Settings \u2192 Developer \u2192 Edit Config',
      '',
      '  Config file locations:',
      '  macOS: ~/Library/Application Support/Claude/claude_desktop_config.json',
      '  Windows: %APPDATA%\\Claude\\claude_desktop_config.json',
      '',
      '{',
      '  "mcpServers": {',
      '    "event-black": {',
      '      "command": "npx",',
      '      "args": ["@event-black/mcp"]',
      '    }',
      '  }',
      '}',
      '',
      'Restart Claude Desktop after saving.',
    ].join('\n'),
  },
  {
    key: 'cursor',
    label: 'Cursor',
    group: 'desktop',
    command: [
      'Option A — Settings UI:',
      '  Cursor Settings \u2192 Tools & MCP \u2192 Add Server',
      '  (Cmd/Ctrl+Shift+P \u2192 "Cursor Settings" if needed)',
      '',
      'Option B — Config file (~/.cursor/mcp.json):',
      '',
      '{',
      '  "mcpServers": {',
      '    "event-black": {',
      '      "url": "https://mcp.event.black"',
      '    }',
      '  }',
      '}',
      '',
      'Restart Cursor after saving.',
    ].join('\n'),
  },
  {
    key: 'windsurf',
    label: 'Windsurf',
    group: 'desktop',
    command: [
      'Option A — Cascade panel:',
      '  Open Cascade (chat) \u2192 Click MCPs icon (top-right)',
      '  \u2192 Add Server \u2192 enter URL',
      '',
      'Option B — Config file:',
      '  macOS/Linux: ~/.codeium/windsurf/mcp_config.json',
      '  Windows: %USERPROFILE%\\.codeium\\windsurf\\mcp_config.json',
      '',
      '{',
      '  "mcpServers": {',
      '    "event-black": {',
      '      "url": "https://mcp.event.black"',
      '    }',
      '  }',
      '}',
    ].join('\n'),
  },
  {
    key: 'vscode',
    label: 'VS Code',
    group: 'desktop',
    command: [
      'Option A — Copilot Chat:',
      '  Open Copilot Chat in Agent Mode',
      '  \u2192 Click the tools icon at the bottom',
      '  \u2192 Add MCP Server \u2192 enter URL',
      '',
      'Option B — Config file (.vscode/mcp.json):',
      '',
      '{',
      '  "servers": {',
      '    "event-black": {',
      '      "type": "http",',
      '      "url": "https://mcp.event.black"',
      '    }',
      '  }',
      '}',
      '',
      'Note: VS Code uses "servers" (not "mcpServers").',
      'Requires Copilot with MCP enabled.',
    ].join('\n'),
  },

  // ── CLI ──
  {
    key: 'claude-code',
    label: 'Claude Code',
    group: 'cli',
    command: 'claude mcp add event-black -- npx @event-black/mcp',
  },
  {
    key: 'chatgpt-cli',
    label: 'ChatGPT CLI',
    group: 'cli',
    command: 'chatgpt mcp add event-black -- npx @event-black/mcp',
  },
  {
    key: 'claw',
    label: 'Claw',
    group: 'cli',
    command: 'claw mcp add event-black -- npx @event-black/mcp',
  },
  {
    key: 'gemini-cli',
    label: 'Gemini CLI',
    group: 'cli',
    command: [
      'Add to ~/.gemini/settings.json:',
      '',
      '{',
      '  "mcpServers": {',
      '    "event-black": {',
      '      "url": "https://mcp.event.black",',
      '      "authProviderType": "dynamic_discovery"',
      '    }',
      '  }',
      '}',
      '',
      'OAuth is handled automatically on first use.',
    ].join('\n'),
  },
];

const GROUPS: ReadonlyArray<{ readonly key: 'web' | 'desktop' | 'cli'; readonly label: string }> = [
  { key: 'web', label: 'Web Apps' },
  { key: 'desktop', label: 'Desktop Apps' },
  { key: 'cli', label: 'CLI' },
];

type McpSetupGuideProps = {
  readonly showApiKeyCallout?: boolean;
};

const McpSetupGuide = ({ showApiKeyCallout = false }: McpSetupGuideProps): ReactElement => {
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handlePlatformClick = (key: string): void => {
    setSelectedPlatform(key);
    setCopied(false);
  };

  const handleCopy = (): void => {
    const platform = PLATFORMS.find((p) => p.key === selectedPlatform);
    if (!platform) return;
    void navigator.clipboard.writeText(platform.command).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 1500);
    });
  };

  return (
    <div className={styles.guide}>
      {showApiKeyCallout && (
        <p className={styles.apiKeyCallout}>
          Looking for an API key? You don&apos;t need one. Your AI assistant handles authentication automatically via OAuth.
        </p>
      )}
      {GROUPS.map((group) => {
        const platforms = PLATFORMS.filter((p) => p.group === group.key);
        return (
          <div key={group.key} className={styles.groupSection}>
            <div className={styles.groupLabel}>{group.label}</div>
            <div className={styles.platformGrid}>
              {platforms.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  data-testid={`mcp-platform-${p.key}`}
                  className={`${styles.platformButton} ${selectedPlatform === p.key ? styles.platformButtonActive : ''}`}
                  onClick={() => { handlePlatformClick(p.key); }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {selectedPlatform && (
        <div className={styles.installCmd}>
          <pre className={styles.installCmdText}>
            {PLATFORMS.find((p) => p.key === selectedPlatform)?.command ?? ''}
          </pre>
          <button type="button" data-testid="mcp-copy" className={styles.installCopyBtn} onClick={handleCopy}>
            {copied ? 'copied!' : 'copy'}
          </button>
        </div>
      )}
    </div>
  );
};

export { McpSetupGuide };
